import {
  Dispatch,
  FormEvent,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useState,
  useRef,
} from "react";
import { BsChevronDown, BsQuestionSquare } from "react-icons/bs";
import { KeyValueObj } from "../../types";
import { SelectWrapper } from "./styles";
import CustomTooltip from "../Tooltip";

export interface IMultiSelectProps {
  options: KeyValueObj[];
  placeholder: string;
  prompt: string;
  fullWidth?: boolean;
  shortWidth?: boolean;
  name: string;
  indexLevel?: number;
  returnId?: boolean;
  tooltip?: string;
  updateValidity?: (name: string, validity: boolean) => void;
  updateData?: (name: string, value: (string | number)[]) => void;
  onEveryChange?: (
    latestInput: (string | number)[],
    name: string,
    updateValidity?: (name: string, validity: boolean) => void,
    setFormValues?: Dispatch<SetStateAction<KeyValueObj>>,
    formValues?: KeyValueObj
  ) => void;
  formValues: KeyValueObj;
  setFormValues: Dispatch<SetStateAction<KeyValueObj>>;
  handleSearch?: (value: string) => void;
  required?: boolean;
  dataTestId?: string;
  readOnly?: boolean;
  initialValues?: (string | number)[];
}

export function MultiSelect({
  options = [],
  placeholder,
  prompt,
  fullWidth = false,
  shortWidth = false,
  name,
  indexLevel = 0,
  returnId = false,
  tooltip,
  updateValidity,
  updateData,
  onEveryChange,
  formValues,
  setFormValues,
  handleSearch,
  required = false,
  dataTestId,
  readOnly = false,
  initialValues = [],
}: IMultiSelectProps) {
  const [selectedValues, setSelectedValues] = useState<KeyValueObj[]>([]);
  const [showOptions, setShowOptions] = useState(false);
  const [selectOptions, setSelectOptions] = useState<KeyValueObj[]>(options);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectOptions(options);
  }, [options]);

  useEffect(() => {
    if (initialValues.length && options.length) {
      const matched = options.filter(
        (opt) =>
          initialValues.includes(opt.id) || initialValues.includes(opt.title)
      );
      setSelectedValues(matched);
      updateValidity?.(name, matched.length > 0);
    }
  }, [options, initialValues]);

  useEffect(() => {
    if (!formValues || !options.length) return;

    const currentValue = formValues[name];

    if (Array.isArray(currentValue) && currentValue.length > 0) {
      // Check if stored values are objects or IDs
      const matched = options.filter((opt) =>
        currentValue.some(
          (v: any) => v?.id === opt.id || v === opt.id || v === opt.title
        )
      );
      setSelectedValues(matched);
    } else {
      // Clear selection when nothing is stored
      setSelectedValues([]);
    }
  }, [formValues, name, options]);

  const toggleDropdown = () => {
    if (!readOnly) setShowOptions((prev) => !prev);
  };

  const handleSelect = (item: KeyValueObj) => {
    if (readOnly) return;

    const exists = selectedValues.some((v) => v.id === item.id);
    const updated = exists
      ? selectedValues.filter((v) => v.id !== item.id)
      : [...selectedValues, item];

    setSelectedValues(updated);

    const valuesToSend = returnId
      ? updated.map((v) => v.id)
      : updated.map((v) => v.title ?? "");

    setFormValues((prev) => ({ ...prev, [name]: valuesToSend }));
    updateData?.(name, valuesToSend);
    onEveryChange?.(
      valuesToSend,
      name,
      updateValidity,
      setFormValues,
      formValues
    );

    updateValidity?.(name, updated.length > 0);
  };

  const handleInputChange = (e: FormEvent<HTMLInputElement>) => {
    const query = e.currentTarget.value;
    if (handleSearch) return handleSearch(query);

    if (!query.trim()) return setSelectOptions(options);

    const filtered = options.filter(
      (option) =>
        option.title?.toLowerCase().includes(query.toLowerCase()) ||
        option.id?.toString().toLowerCase().includes(query.toLowerCase())
    );
    setSelectOptions(filtered);
  };

  function onOptionKeyDown(e: KeyboardEvent<HTMLLIElement>, item: KeyValueObj) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect(item);
    } else if (e.key === "Escape") {
      setShowOptions(false);
    }
  }

  return (
    <SelectWrapper
      ref={wrapperRef}
      shortWidth={shortWidth}
      indexLevel={indexLevel}
      showOptions={showOptions}
      fullWidth={fullWidth}
      fadeOut={false}
    >
      <div className="label-wrapper">
        <label>
          {prompt} {required && <span className="text-red-500">*</span>}
        </label>
        {tooltip && (
          <CustomTooltip title={tooltip}>
            <BsQuestionSquare className="tooltip-icon" />
          </CustomTooltip>
        )}
      </div>

      <div className="select-wrapper">
        <div className="select-input" onClick={toggleDropdown}>
          {showOptions && !readOnly ? (
            <input
              onChange={handleInputChange}
              autoFocus
              type="text"
              placeholder="Search..."
              className="border-none outline-none w-full"
            />
          ) : selectedValues.length > 0 ? (
            <div className="flex flex-wrap gap-1 min-h-[40px]">
              {selectedValues.map((item) => (
                <span
                  key={item.id}
                  className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm"
                >
                  {item.title}
                </span>
              ))}
            </div>
          ) : (
            <h4
              id={name}
              data-testid={dataTestId}
              className="cursor-pointer text-gray-500"
            >
              {placeholder}
            </h4>
          )}
          {!readOnly && <BsChevronDown className="icon" />}
        </div>

        {showOptions && !readOnly && (
          <ul
            className="select-options max-h-48 overflow-y-auto border border-gray-200 bg-white rounded-md mt-1 shadow-sm"
            role="listbox"
          >
            {selectOptions.length > 0 ? (
              selectOptions.map((item, i) => {
                const checked = selectedValues.some((v) => v.id === item.id);
                return (
                  <li
                    key={`${name}-${i}`}
                    onClick={() => handleSelect(item)}
                    onKeyDown={(e) => onOptionKeyDown(e, item)}
                    tabIndex={0}
                    role="option"
                    aria-selected={checked}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-2"
                  >
                    <input type="checkbox" readOnly checked={checked} />
                    {item.title}
                  </li>
                );
              })
            ) : (
              <li className="p-2 text-gray-400">No results found</li>
            )}
          </ul>
        )}
      </div>
    </SelectWrapper>
  );
}
